import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, Check, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const ENDPOINT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/buddy-api`;

const exampleResponse = `{
  "ok": true,
  "remaining_requests_today": 9,
  "data": {
    "price": {
      "btc_usd": "97500",
      "macro_mode": "bull",
      "display_macro_state": "Bullish Trend"
    },
    "network": {
      "state": "healthy",
      "block_state": "normal",
      "heat_level": 3
    },
    "mood": {
      "buddy_mood": "happy",
      "mood_label": "Optimistic",
      "network_mood": "calm",
      "market_mood": "bullish",
      "block_mood": "steady"
    },
    "weather": {
      "condition": "sunny",
      "description": "Clear skies ahead"
    },
    "sentiment": {
      "value": 0.72,
      "bucket": "greedy"
    },
    "context": {
      "verdict": "Stack sats!",
      "day_archetype": "accumulation",
      "daily_summary": "...",
      "weekly_vibe": "optimistic",
      "plain_subtitle": "...",
      "macro_explanation": "...",
      "what_it_means": "...",
      "footer_line": "..."
    },
    "season": {
      "state": "bull",
      "deviation_pct": 12.5,
      "intensity": 0.65
    },
    "long_term": {
      "confidence": "high",
      "status_label": "Healthy",
      "scores": { ... }
    },
    "macro_advisor": { ... },
    "updated_at": "2026-04-01T12:00:00Z"
  }
}`;

const curlExample = `curl -X GET "${ENDPOINT_URL}" \\
  -H "x-api-key: YOUR_API_KEY"`;

const jsExample = `const res = await fetch("${ENDPOINT_URL}", {
  headers: { "x-api-key": "YOUR_API_KEY" }
});
const data = await res.json();
console.log(data);`;

const pythonExample = `import requests

res = requests.get(
    "${ENDPOINT_URL}",
    headers={"x-api-key": "YOUR_API_KEY"}
)
print(res.json())`;

function CopyBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border rounded-t-md">
        <span className="text-xs font-mono text-muted-foreground">{lang}</span>
        <button onClick={copy} className="text-muted-foreground hover:text-foreground transition-colors">
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <pre className="p-4 bg-muted/30 rounded-b-md overflow-x-auto text-sm font-mono text-foreground whitespace-pre-wrap break-all">
        {code}
      </pre>
    </div>
  );
}

export default function ApiDocs() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Takaisin
          </button>
          <h1 className="text-3xl font-bold tracking-tight">Mempool Buddy API</h1>
          <p className="text-muted-foreground">Hae Buddyn senhetkinen tila – hinta, verkko, moodi, sää ja enemmän – yhdellä API-kutsulla.</p>
        </div>

        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Endpoint</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="font-mono text-xs bg-green-500/10 text-green-400 border-green-500/30">GET</Badge>
              <code className="text-sm font-mono text-foreground break-all">/functions/v1/buddy-api</code>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Autentikointi</h3>
              <p className="text-sm text-muted-foreground">Lähetä API-avain <code className="bg-muted px-1.5 py-0.5 rounded text-xs">x-api-key</code> -headerissa.</p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Rate limit</h3>
              <p className="text-sm text-muted-foreground">Enintään <strong>10 pyyntöä / päivä</strong> per avain. Laskuri nollautuu keskiyöllä (UTC).</p>
            </div>
          </CardContent>
        </Card>

        {/* Status codes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Statuskoodit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm">
              {[
                ["200", "OK – data palautettu onnistuneesti", "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"],
                ["401", "Puuttuva tai virheellinen API-avain", "bg-destructive/10 text-destructive border-destructive/30"],
                ["429", "Päivittäinen rate limit ylitetty", "bg-amber-500/10 text-amber-400 border-amber-500/30"],
                ["503", "Buddy-tilaa ei saatavilla", "bg-orange-500/10 text-orange-400 border-orange-500/30"],
              ].map(([code, desc, cls]) => (
                <div key={code} className="flex items-start gap-3">
                  <Badge variant="outline" className={`font-mono text-xs shrink-0 ${cls}`}>{code}</Badge>
                  <span className="text-muted-foreground">{desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Examples */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Esimerkkikutsut</CardTitle>
            <CardDescription>Korvaa <code className="bg-muted px-1 py-0.5 rounded text-xs">YOUR_API_KEY</code> omalla avaimellasi.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyBlock code={curlExample} lang="cURL" />
            <CopyBlock code={jsExample} lang="JavaScript" />
            <CopyBlock code={pythonExample} lang="Python" />
          </CardContent>
        </Card>

        {/* Response */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Esimerkkivastaus</CardTitle>
            <CardDescription>JSON-vastaus onnistuneesta kutsusta.</CardDescription>
          </CardHeader>
          <CardContent>
            <CopyBlock code={exampleResponse} lang="JSON" />
          </CardContent>
        </Card>

        {/* Response fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vastauksen kentät</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {[
                ["price", "BTC-hinta, makrotila ja trendi"],
                ["network", "Verkon tila, blokkitilanne ja kuumotustaso (0–10)"],
                ["mood", "Buddyn moodi sekä verkko-, markkina- ja blokkimoodi"],
                ["weather", "Sää-metafora ja kuvaus markkinatilanteesta"],
                ["sentiment", "Fear & Greed -arvo (0–1) ja luokitus"],
                ["context", "Päivän tuomio, arkkityyppi, yhteenvedot ja selitykset"],
                ["season", "Markkinakausi (bull/bear/neutral), poikkeama ja intensiteetti"],
                ["long_term", "Pitkän aikavälin luottamus ja pistemäärät"],
                ["macro_advisor", "Makroneuvonantajan analyysi (jos saatavilla)"],
                ["updated_at", "Tilan viimeisimmän päivityksen aikaleima"],
              ].map(([field, desc]) => (
                <div key={field} className="flex items-start gap-3">
                  <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono shrink-0">{field}</code>
                  <span className="text-muted-foreground">{desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground pb-8">
          API-avaimia hallinnoidaan osoitteessa <button onClick={() => navigate("/admin/api-keys")} className="underline hover:text-foreground transition-colors">/admin/api-keys</button>
        </p>
      </div>
    </div>
  );
}
